import { stringify } from "qs";
import { Pagination } from "types/Models/pagination";
import {
  ConfirmBulkProductRow,
  ConfirmBulkProductsResult,
  ItemType,
  PreviewBulkProductsResult,
  ProductAndServices,
  ProductAndServicesDetail,
  ProductAndServicesList,
  TaxRate,
} from "types/Models/productAndServices";
import { emptyApi } from "./emptyApi";

const productAndServicesApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getProductAndServices: build.query<
      Pagination & {
        products: ProductAndServicesList[];
        revenue_category_id: string;
      },
      {
        groupId: string;
        entityId: string;
        pageNum?: number;
        searchTerm?: string | null;
        sources?: string;
        productTypes?: string;
        productCategories?: string;
        sortCol?: "PRICE" | "NAME";
        sortOrder?: "ASC" | "DSC";
      }
    >({
      query: ({
        groupId,
        entityId,
        pageNum,
        searchTerm,
        sources,
        productTypes,
        productCategories,
        sortCol,
        sortOrder,
      }) => {
        const query = stringify(
          {
            page_num: pageNum,
            search_term: searchTerm || null,
            sources: sources || null,
            product_types: productTypes || null,
            product_categories: productCategories || null,
            sort_col: sortCol || null,
            sort_order: sortOrder || null,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/products/${query}`,
        };
      },
      providesTags: (res) => (res ? [{ type: "PRODUCTS_AND_SERVICES" }] : []),
    }),

    addProductAndService: build.mutation<
      ProductAndServicesDetail,
      {
        groupId: string;
        entityId: string;
        payload: {
          name: string;
          prices: {
            price: number;
            description: string;
            is_default: boolean;
            frequency: string;
          }[];
          product_category: string;
          transaction_category_id: null | string;
          type: ItemType;
          classification_ids?: string[];
          default_tax_rate_id?: string | null;
          sales_tax_code_id?: string | null;
        };
      }
    >({
      query: ({ groupId, entityId, payload }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/products/`,
        body: payload,
        method: "POST",
      }),
      invalidatesTags: (res) =>
        res ? [{ type: "PRODUCTS_AND_SERVICES" }] : [],
    }),

    getTaxRates: build.query<TaxRate[], { groupId: string; entityId: string }>({
      query: ({ groupId, entityId }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/tax-rates/`,
      }),
      providesTags: (res) => (res ? [{ type: "TAX_RATES" }] : []),
    }),

    createTaxRate: build.mutation<
      TaxRate,
      {
        groupId: string;
        entityId: string;
        payload: { description: string; rate: number };
      }
    >({
      query: ({ groupId, entityId, payload }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/tax-rates/`,
        body: payload,
        method: "POST",
      }),
      invalidatesTags: (res) => (res ? [{ type: "TAX_RATES" }] : []),
    }),

    updateTaxRate: build.mutation<
      TaxRate,
      {
        groupId: string;
        entityId: string;
        taxRateId: string;
        payload: { description?: string; rate?: number };
      }
    >({
      query: ({ groupId, entityId, taxRateId, payload }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/tax-rates/${taxRateId}/`,
        body: payload,
        method: "PATCH",
      }),
      invalidatesTags: (res) => (res ? [{ type: "TAX_RATES" }] : []),
    }),

    getProductAndServiceById: build.query<
      ProductAndServicesDetail,
      { groupId: string; entityId: string; productId: string }
    >({
      query: ({ groupId, entityId, productId }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/products/${productId}/`,
      }),
      providesTags: (res) => (res ? [{ type: "PRODUCTS_AND_SERVICES" }] : []),
    }),

    updateProductAndService: build.mutation<
      ProductAndServicesDetail,
      {
        groupId: string;
        entityId: string;
        productId: string;
        payload: {
          name?: string;
          prices?: {
            uuid?: string;
            price: number;
            description: string;
            is_default: boolean;
            frequency: string;
          }[];
          product_category?: string;
          transaction_category_id?: string;
          type?: ItemType;
          classification_ids?: string[];
          default_tax_rate_id?: string | null;
          sales_tax_code_id?: string | null;
        };
      }
    >({
      query: ({ groupId, entityId, productId, payload }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/products/${productId}/`,
        body: payload,
        method: "PATCH",
      }),
      invalidatesTags: (res) =>
        res ? [{ type: "PRODUCTS_AND_SERVICES" }] : [],
    }),

    deleteProductAndService: build.mutation<
      ProductAndServices,
      {
        groupId: string;
        entityId: string;
        productId: string;
      }
    >({
      query: ({ groupId, entityId, productId }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/products/${productId}/`,
        method: "DELETE",
      }),
      invalidatesTags: (res) =>
        res ? [{ type: "PRODUCTS_AND_SERVICES" }] : [],
    }),

    bulkDeleteProducts: build.mutation<
      { deleted_product_ids: string[]; skipped_product_ids: string[] },
      { groupId: string; entityId: string; productIds: string[] }
    >({
      query: ({ groupId, entityId, productIds }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/products/bulk/`,
        method: "DELETE",
        body: { product_ids: productIds },
      }),
      invalidatesTags: [{ type: "PRODUCTS_AND_SERVICES" }],
    }),

    getProductCategories: build.query<
      { name: string; type: string; uuid: string }[],
      void
    >({
      query: () => {
        return {
          url: `/api/inkle/invoicing/product_categories/`,
        };
      },
      providesTags: (res) => (res ? [{ type: "PRODUCTS_AND_SERVICES" }] : []),
    }),

    getInfiniteGroupByProductsServices: build.infiniteQuery<
      Pagination & {
        products: ProductAndServices[];
        services: ProductAndServices[];
      },
      {
        groupId: string;
        entityId: string;
        search_term?: string;
        type?: ItemType;
      },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (lastPageParam < (lastPage.total_pages || 1)) {
            return lastPageParam + 1;
          }
          return undefined;
        },
        getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
          return firstPageParam > 1 ? firstPageParam - 1 : undefined;
        },
      },
      query({ queryArg, pageParam }) {
        const { groupId, entityId, search_term, type } = queryArg;
        const query = stringify(
          {
            search_term: search_term || null,
            type: type || null,
            page_num: pageParam,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/products_by_type/${query}`,
        };
      },
      providesTags: (res) => (res ? [{ type: "PRODUCTS_AND_SERVICES" }] : []),
    }),

    uploadBulkProducts: build.mutation<
      { bulk_file_id: string },
      { groupId: string; entityId: string; file: File; signal?: AbortSignal }
    >({
      query: ({ groupId, entityId, file, signal }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/products/bulk_csv/`,
          method: "POST",
          body: formData,
          signal,
        };
      },
    }),

    previewBulkProducts: build.query<
      PreviewBulkProductsResult,
      {
        groupId: string;
        entityId: string;
        bulkFileId: string;
      }
    >({
      query: ({ groupId, entityId, bulkFileId }) => {
        const query = stringify(
          { bulk_file_id: bulkFileId },
          { addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/products/preview_bulk_csv/${query}`,
        };
      },
    }),

    confirmBulkProducts: build.mutation<
      ConfirmBulkProductsResult,
      {
        groupId: string;
        entityId: string;
        products: ConfirmBulkProductRow[];
        signal?: AbortSignal;
      }
    >({
      query: ({ groupId, entityId, products, signal }) => ({
        url: `/api/inkle/invoicing/group/${groupId}/entity/${entityId}/products/confirm_bulk_csv/`,
        method: "POST",
        body: { products },
        signal,
      }),
      invalidatesTags: (res) =>
        res ? [{ type: "PRODUCTS_AND_SERVICES" }] : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProductAndServicesQuery,
  useAddProductAndServiceMutation,
  useGetProductAndServiceByIdQuery,
  useLazyGetProductAndServiceByIdQuery,
  useUpdateProductAndServiceMutation,
  useDeleteProductAndServiceMutation,
  useBulkDeleteProductsMutation,
  useGetProductCategoriesQuery,
  useGetInfiniteGroupByProductsServicesInfiniteQuery,
  useGetTaxRatesQuery,
  useCreateTaxRateMutation,
  useUpdateTaxRateMutation,
  useUploadBulkProductsMutation,
  useLazyPreviewBulkProductsQuery,
  useConfirmBulkProductsMutation,
} = productAndServicesApi;
